import { appError, err, ok, unwrap, } from '@orbit-mind/shared-types';
import { attachmentSchema } from '@orbit-mind/validators';
class FileSyncFailure extends Error {
    failure;
    constructor(failure) {
        super(failure.message);
        this.failure = failure;
    }
}
function linkedFiles(document) {
    const ids = new Set();
    for (const node of Object.values(document.nodes)) {
        for (const id of node.attachments ?? [])
            ids.add(id);
        const content = node.metadata?.content;
        if (typeof content === 'object' &&
            content !== null &&
            !Array.isArray(content) &&
            'attachmentId' in content &&
            typeof content.attachmentId === 'string')
            ids.add(content.attachmentId);
    }
    return ids;
}
/** Sequential, bounded transfers; a failed file never gets an acknowledged sync badge. */
export async function syncAttachments(options) {
    const linked = options.document === undefined ? null : linkedFiles(options.document);
    if (linked?.size === 0)
        return ok(undefined);
    const path = `${options.origin ?? ''}/sync/maps/${encodeURIComponent(options.mapId)}/attachments`;
    const headers = options.accountId == null ? {} : { 'x-orbit-account': options.accountId };
    async function request(url, init = {}) {
        const requestHeaders = new Headers(init.headers);
        for (const [key, value] of Object.entries(headers))
            requestHeaders.set(key, value);
        const response = await options.fetch(url, {
            ...init,
            credentials: 'same-origin',
            headers: requestHeaders,
            signal: AbortSignal.timeout(60000),
        });
        if (!response.ok) {
            let body = {};
            try {
                body = (await response.json());
            }
            catch {
                /* Status remains authoritative. */
            }
            throw new FileSyncFailure(appError(response.status === 413 ? 'QUOTA_EXCEEDED' : 'NETWORK_UNAVAILABLE', typeof body.message === 'string'
                ? body.message
                : `Files could not sync (${String(response.status)}).`, {
                retryable: body.retryable === false ? false : response.status >= 500 || response.status === 429,
            }));
        }
        return response;
    }
    try {
        const response = await request(path);
        const body = await response.json();
        if (typeof body !== 'object' ||
            body === null ||
            !('attachments' in body) ||
            !Array.isArray(body.attachments))
            throw new Error('Invalid file listing');
        const remote = body.attachments.map((item) => attachmentSchema.parse(item));
        if (remote.some((file) => file.mapId !== options.mapId))
            throw new Error('Invalid file map');
        const local = unwrap(await options.store.listForMap(options.mapId)).attachments;
        const failures = [];
        async function transfer(work) {
            try {
                await work();
            }
            catch (cause) {
                failures.push(cause instanceof FileSyncFailure
                    ? cause.failure
                    : appError('STORAGE_UNAVAILABLE', cause instanceof Error ? cause.message : 'File unavailable', { retryable: false }));
            }
        }
        if (local.length === 0 && remote.length === 0)
            return ok(undefined);
        if (options.upload !== false) {
            for (const file of local) {
                if (linked !== null && !linked.has(file.id))
                    continue;
                await transfer(async () => {
                    const found = remote.find((item) => item.id === file.id);
                    if (found !== undefined) {
                        if (found.checksum !== file.checksum)
                            throw new Error('Conflicting attachment content');
                        return;
                    }
                    const bytes = unwrap(await options.store.bytesOf(file.storageKey));
                    if (bytes === undefined)
                        throw new Error(`File unavailable: ${file.filename}`);
                    await request(path, {
                        method: 'POST',
                        headers: {
                            'content-type': 'application/octet-stream',
                            'x-orbit-attachment': encodeURIComponent(JSON.stringify(file)),
                        },
                        body: bytes instanceof Uint8Array ? new Uint8Array(bytes) : bytes,
                    });
                });
            }
        }
        for (const file of remote) {
            await transfer(async () => {
                const existing = local.find((item) => item.id === file.id);
                if (existing !== undefined && existing.checksum !== file.checksum)
                    throw new Error('Conflicting attachment content');
                if (existing?.checksum === file.checksum &&
                    unwrap(await options.store.bytesOf(existing.storageKey)) !== undefined)
                    return;
                if (file.size > 25 * 1024 * 1024)
                    throw new Error('File too large');
                const download = await request(`${path}?id=${encodeURIComponent(file.id)}`);
                // React Native fetch does not expose ReadableStream. Its bounded server response is checked again before storage.
                const bytes = new Uint8Array(await download.arrayBuffer());
                unwrap(await options.store.receive(file, bytes));
            });
        }
        const failure = failures[0];
        return failure === undefined
            ? ok(undefined)
            : err({ ...failure, retryable: failures.some((item) => item.retryable) });
    }
    catch (cause) {
        if (cause instanceof FileSyncFailure)
            return err(cause.failure);
        return err(appError('NETWORK_UNAVAILABLE', cause instanceof Error ? cause.message : 'Files could not sync', { retryable: true }));
    }
}
/** File transfers run independently through SyncEngine.syncFiles. */
export function withAttachments(transport, _options) {
    return transport;
}
//# sourceMappingURL=attachments.js.map